import { Link } from "@tanstack/react-router";
import { Button, Checkbox, Popover, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { EmployeeTab } from "@/modules/employees/utils/tabs";
import { ATTENDANCE_STATUS_RANG, DayStrip, holatVar } from "@/shared/charts";
import { MAIN_SCROLL_ID } from "@/shared/components/layouts/MainLayout";
import type { ReportDateRange } from "../hooks/useReportFilters";
import type { ReportAnalytics, ReportColumn, ReportRow } from "../types";
import { matritsaSanalari, sanaBoyichaHolat } from "../utils/matrixDays";
import { columnTitle } from "../utils/reportI18n";
import { davomatFoizi, foizMatni } from "../utils/summaryDerive";

/** Bo'sh katak belgisi — `0` dan ATAYLAB farqli. */
const BOSH = "—";

export interface BooleanLabels {
	yes: string;
	no: string;
}

/** Eksport (`packages/core/src/export/writer.ts`) bilan bir xil standart. */
const STANDART_YORLIQLAR: BooleanLabels = { yes: "Ha", no: "Yo'q" };

/**
 * Enum qiymatli matn ustunlari → tarjima prefiksi.
 *
 * ⚠️ Server bu ustunlarda XOM enum qaytaradi (`bekor_qilingan`,
 * `mock_provider`, `high`) — eksport uni o'zbekcha matn sifatida
 * yozadi, panel esa joriy tilga o'giradi. Kalit topilmasa qiymat
 * o'zgarmaydi (`defaultValue`) — yangi enum qiymati bo'sh katak
 * bo'lib yo'qolmasin. Prefiks `analytics.breakdown` — diagramma
 * legendalari bilan BIR XIL nom.
 */
const ENUM_TARJIMA: Record<string, string> = {
	status: "analytics.breakdown",
	kind: "analytics.breakdown",
	severity: "cell.severity",
};

/** Ichki qator kaliti — descriptor ustunlari bilan TO'QNASHMAYDI. */
const RAD_KALIT = "__skudRowKey";

/**
 * Hisobot katagini matnga aylantiradi.
 *
 * ⚠️⚠️ **`duration` da ishora OLDIN ajratiladi.**
 * `Math.floor(-90 / 60)` = `-2`, qoldiq esa `Math.abs(-90 % 60)` =
 * `30` — belgi ikki marta hisobga olinib `-2:30` chiqardi (to'g'risi
 * `-1:30`). Natija ISHONARLI ko'rinadi, ya'ni xato JIM. Bu aynan
 * poydevor Task 1 da eksport tomonida topilgan nuqson; formula
 * `packages/core/src/export/writer.ts` dagi `formatDuration` bilan
 * AYNAN bir xil bo'lishi shart, aks holda panel va Excel manfiy
 * qiymatda ajralardi.
 *
 * ⚠️ **`null` BIRINCHI tekshiriladi** — `formatCell` (eksport) ham
 * shunday qiladi. `qiymat ? "Ha" : "Yo'q"` shakli «ma'lumot yo'q» ni
 * «Yo'q» ga aylantirib, ikki iste'molchini jimgina ajratardi.
 *
 * ⚠️ **`@skud/core` dan import QILINMAYDI** — frontend unga
 * bog'lanmagan (`apps/frontend/package.json`: faqat `@skud/domain` va
 * `@skud/shared`) va `core` da Redis, BullMQ, `pino` bor. Formula
 * ataylab nusxalangan, test esa ikki nusxani bog'lab turadi.
 *
 * ⚠️ **Mantiqiy yorliqlar — `labels` parametri** (spec §2.6). Jadval
 * ularni `reports.json` ning `cell.yes`/`cell.no` idan beradi; standart
 * qiymat eksport yozuvchisidagi o'zbekcha matn bilan bir xil qoladi —
 * funksiya Hook chaqira olmaydi va i18next'ga bog'lanmaydi.
 */
export function formatReportCell(
	qiymat: unknown,
	tip: ReportColumn["type"],
	labels: BooleanLabels = STANDART_YORLIQLAR
): string {
	if (qiymat === null || qiymat === undefined) {
		return BOSH;
	}

	if (tip === "boolean") {
		return qiymat ? labels.yes : labels.no;
	}

	if (tip === "duration") {
		const daqiqa = Number(qiymat);

		if (!Number.isFinite(daqiqa)) {
			return BOSH;
		}

		const manfiy = daqiqa < 0;
		const mutlaq = Math.abs(daqiqa);
		const soat = Math.floor(mutlaq / 60);

		return `${manfiy ? "-" : ""}${soat}:${String(mutlaq % 60).padStart(2, "0")}`;
	}

	if (tip === "date") {
		return String(qiymat).slice(0, 10);
	}

	if (tip === "datetime") {
		const sana = dayjs(String(qiymat));

		return sana.isValid() ? sana.format("YYYY-MM-DD HH:mm") : BOSH;
	}

	return String(qiymat);
}

/**
 * ⚠️ `tab` qiymati `EMPLOYEE_TABS` tipiga BOG'LANADI, literal satr
 * sifatida yozilmaydi. `resolveTab` CHAQIRILMAYDI — uning imzosi
 * profil blokida o'zgarmoqda.
 */
const DAVOMAT_TAB: EmployeeTab = "davomat";

/** Hosila «Davomat» ustunidagi minibarning kengligi (kanvas naqshi). */
const MINIBAR_PX = 64;

/** Kun chizig'i ustunining kengligi, px (kanvas «21 kun» ustuni). */
const KUN_CHIZIGI_PX = 180;

const MINIBAR_RANGI = "rgb(42, 120, 214)"; // #2a78d6
const MINIBAR_TREGI = "rgb(238, 239, 236)"; // #eeefec

/**
 * Qator qiymatidan xodim ID sini oladi.
 *
 * ⚠️ **`employeeId` YO'Q bo'lganda havola CHIZILMAYDI.**
 * `attendance_monthly` qatorlarida u bor (descriptor ustuni bo'lmasa
 * ham javobga o'tadi), boshqa hisobotlarda bo'lmasligi mumkin —
 * `undefined` bilan `<Link>` chizilsa marshrut yiqilardi.
 */
function xodimId(qator: ReportRow): string | null {
	const qiymat = qator.employeeId;

	return typeof qiymat === "string" && qiymat.length > 0 ? qiymat : null;
}

/**
 * Sticky sarlavhaning scroll konteyneri — layout faqat
 * `MAIN_SCROLL_ID` ni aylantiradi (`MainLayout`, `overflow-hidden`
 * ildiz); `window` ga bog'langan sticky u yerda JIMGINA ishlamasdi.
 */
function scrollKonteyneri(): HTMLElement | Window {
	return document.getElementById(MAIN_SCROLL_ID) ?? window;
}

/** Hosila «Davomat» ustunining kaliti — ustun tanlovida ham shu. */
const RATE_KALIT = "__skudRate";

function sonQiymati(qiymat: unknown): number | null {
	return typeof qiymat === "number" && Number.isFinite(qiymat) ? qiymat : null;
}

interface Props {
	columns: ReportColumn[];
	rows: ReportRow[];
	loading: boolean;
	meta: { total: number; page: number; limit: number; totalPages: number };
	onPageChange: (page: number, limit: number) => void;
	/** Xodim havolasi olib o'tadigan oy filtri. */
	range: ReportDateRange;
	/**
	 * ⚠️ Hosila «Davomat» ustuni FAQAT xulosali hisobotda
	 * (`descriptor.hasSummary`): xulosasiz hisobotda `countedDays` /
	 * `presentDays` ustunlari umuman yo'q va ustun har qatorda «—»
	 * bo'lib turardi.
	 */
	showRate: boolean;
	/**
	 * Hisobot kaliti — ustun sarlavhasining kind'ga xos tarjimasi uchun
	 * (`columnTitle`: `leads` da `employeeName` «Kim kiritdi»).
	 */
	kind?: string;
	/**
	 * `attendance_monthly` analitikasining `matrix` i — «N kun» chizig'i
	 * ustuni uchun (reja F2). Ustun FAQAT matritsa bo'sh bo'lmaganda
	 * chiziladi: boshqa hisobotlarda konvert `matrix: []` qaytaradi, ya'ni
	 * `kind` literali bilan darvozalash shart emas (`hasSummary` qoidasi).
	 */
	matrix?: ReportAnalytics["matrix"];
}

/**
 * ⚠️ **Ustunlar SERVERDAN keladi** (`data.columns`), frontendda qattiq
 * kodlanmaydi — yangi hisobot qo'shilganda UI o'zgarmasligi kerak
 * (spec §4.1).
 *
 * ⚠️ `scroll={{ x: "max-content" }}` — hisobotda ustun ko'p va
 * gorizontal aylantirish kerak (`DataTable` naqshi).
 *
 * ⚠️ `DataTable` ISHLATILMAYDI: u `usePaginatedQuery` natijasini
 * kutadi, hisobot javobi esa qo'shimcha `columns` maydonini olib
 * keladi va sahifalash holati sahifada yashaydi.
 *
 * ⚠️⚠️ **Hosila «Davomat» ustuni EKSPORT FAYLIGA TUSHMAYDI** —
 * `packages/core/src/export/reportExport.ts` sarlavhani
 * `descriptor.columns` dan quradi va bu ustun `columns` massividan
 * KEYIN, mijozda qo'shiladi. Farq ONGLI: «Excel'da ustun yo'q» deb
 * nuqson ochilmasligi uchun shu izoh yozilgan.
 *
 * ⚠️ **«Jami kechikish» katagi `1:48` bo'lib qoladi, «108 daq»
 * EMAS** (kanvas «108 daq» yozgan). Descriptor uni `type: "duration"`
 * deb e'lon qiladi va `formatReportCell` formulasi
 * `packages/core/src/export/writer.ts` ning `formatDuration` i bilan
 * BOG'LANGAN — panel va Excel manfiy qiymatda ajralmasligi uchun. KPI
 * kartasi esa «4 218 daq» formatida qoladi: ONGLI ajralish.
 */
export function ReportTable({
	columns,
	rows,
	loading,
	meta,
	onPageChange,
	range,
	showRate,
	kind,
	matrix,
}: Props) {
	const { t } = useTranslation("reports");
	// ⚠️ Faqat YASHIRILGANLAR saqlanadi — server yangi ustun qo'shsa u
	// avtomatik ko'rinadi.
	const [yashirin, setYashirin] = useState<ReadonlySet<string>>(new Set());

	const mantiqiyYorliqlar: BooleanLabels = { yes: t("cell.yes"), no: t("cell.no") };

	function katakMatni(qiymat: unknown, tip: ReportColumn["type"]): string {
		return formatReportCell(qiymat, tip, mantiqiyYorliqlar);
	}

	const antdColumns: ColumnsType<ReportRow> = columns.map((ustun) => ({
		title: columnTitle(t, ustun, kind),
		key: ustun.key,
		dataIndex: ustun.key,
		render: (qiymat: unknown, qator: ReportRow) => {
			const prefiks = ENUM_TARJIMA[ustun.key];

			if (prefiks && typeof qiymat === "string" && qiymat !== "") {
				return t(`${prefiks}.${qiymat}`, { defaultValue: qiymat });
			}

			if (ustun.key !== "employeeName") {
				return katakMatni(qiymat, ustun.type);
			}

			const id = xodimId(qator);

			// ⚠️ Havola oy filtrini OLIB O'TADI — kanvasdagi «shu oy filtri
			// bilan ochiladi» va'dasi. `dateFrom`/`dateTo` ni profil bugun
			// o'qimaydi, lekin ular ZARARSIZ (`validateSearch` hech qayerda
			// yo'q) va profil ularni o'qiy boshlagan kunda havola
			// o'z-o'zidan to'liq ishlaydi.
			return id ? (
				<Link
					to="/employees/$id"
					params={{ id }}
					search={{ tab: DAVOMAT_TAB, dateFrom: range.dateFrom, dateTo: range.dateTo }}
					className="text-[#1c5cab] hover:underline"
				>
					{katakMatni(qiymat, ustun.type)}
				</Link>
			) : (
				katakMatni(qiymat, ustun.type)
			);
		},
	}));

	if (showRate) {
		// ⚠️ Ustun `columns` massividan KEYIN qo'shiladi — «ustunlar
		// SERVERDAN keladi» qoidasi buzilmaydi, bu HOSILA ustun.
		antdColumns.push({
			title: t("table.rateColumn"),
			key: RATE_KALIT,
			align: "right",
			width: 150,
			render: (_qiymat: unknown, qator: ReportRow) => {
				const foiz = davomatFoizi(
					sonQiymati(qator.presentDays) ?? 0,
					sonQiymati(qator.countedDays) ?? 0
				);

				return (
					<span className="inline-flex items-center gap-2 justify-end w-full">
						<span
							className="block h-1.5 rounded-full overflow-hidden"
							style={{ width: MINIBAR_PX, backgroundColor: MINIBAR_TREGI }}
						>
							<span
								className="block h-full"
								style={{
									width: `${(foiz ?? 0) * 100}%`,
									backgroundColor: MINIBAR_RANGI,
								}}
							/>
						</span>
						{/*
						 * ⚠️ Format `foizMatni()` dan — KPI kartasi va chop etish
						 * sarlavhasi bilan AYNAN bir xil satr. Ikki nusxa bo'lsa,
						 * biri `0%` ni, ikkinchisi `—` ni ko'rsatardi.
						 */}
						<b className="tabular-nums">{foizMatni(foiz)}</b>
					</span>
				);
			},
		});
	}

	if (matrix && matrix.length > 0) {
		antdColumns.push(kunChizigiUstuni(matrix, t));
	}

	// ⚠️ Hisobot qatorining barqaror ID si YO'Q (agregat hisobotda u
	// umuman mavjud emas), `rowKey` ning indeks shakli esa antd 6 da
	// eskirgan — kalit shu sababli qatorga OLDINDAN yoziladi. Qo'shimcha
	// maydon jadvalda ko'rinmaydi: ustunlar descriptor'dan quriladi va
	// `RAD_KALIT` ularning hech biriga mos kelmaydi.
	const kalitliQatorlar = rows.map((qator, index) => ({ ...qator, [RAD_KALIT]: index }));

	const tanlovVariantlari = antdColumns.map((ustun) => ({
		value: String(ustun.key),
		label: typeof ustun.title === "string" ? ustun.title : String(ustun.key),
	}));
	const korinadigan = antdColumns.filter((ustun) => !yashirin.has(String(ustun.key)));

	// ⚠️ Saralash sarlavhasi YO'Q: server tartibni o'zi beradi va joriy
	// sahifani mijozda saralash butun kesim tartibi deb o'qilardi.
	return (
		<div>
			<div className="flex justify-end px-3 py-2 skud-no-print">
				<Popover
					trigger="click"
					placement="bottomRight"
					content={
						<Checkbox.Group
							data-testid="column-picker"
							className="flex flex-col gap-1"
							options={tanlovVariantlari}
							value={tanlovVariantlari.map((v) => v.value).filter((kalit) => !yashirin.has(kalit))}
							onChange={(tanlangan) => {
								const korinsin = new Set(tanlangan.map(String));
								setYashirin(
									new Set(
										tanlovVariantlari.map((v) => v.value).filter((kalit) => !korinsin.has(kalit))
									)
								);
							}}
						/>
					}
				>
					<Button size="small" data-testid="columns-button">
						{t("table.columnsButton")}
					</Button>
				</Popover>
			</div>

			<Table<ReportRow>
				columns={korinadigan}
				sticky={{ getContainer: scrollKonteyneri }}
				dataSource={kalitliQatorlar}
				loading={loading}
				rowKey={RAD_KALIT}
				scroll={{ x: "max-content" }}
				size="small"
				pagination={{
					current: meta.page,
					pageSize: meta.limit,
					total: meta.total,
					showSizeChanger: true,
					pageSizeOptions: [20, 50, 100, 200],
					showTotal: (total, range) =>
						t("table.showTotal", { from: range[0], to: range[1], total }),
					onChange: onPageChange,
				}}
			/>
		</div>
	);
}

/**
 * Hosila «N kun» ustuni — matritsadagi xodimning oynadagi kunlari.
 *
 * ⚠️ **Faqat matritsadagi xodimlar** (`matrixLimit` ta eng past
 * davomatli). Qolgan qatorlarda katak BO'SH — bu ma'lumot yo'qligi
 * emas, analitika faqat shu xodimlar kunlarini qaytaradi (reja F2).
 *
 * ⚠️ Sanalar — matritsa BIRLASHMASI (`matritsaSanalari`), xodimda yo'q
 * sana `--chart-grid` katak: chiziqlar ustma-ust tushadi.
 *
 * ⚠️⚠️ `__skudRate` kabi bu ham EKSPORTGA TUSHMAYDI — ustun
 * `columns` massividan keyin, mijozda qo'shiladi.
 */
function kunChizigiUstuni(
	matrix: ReportAnalytics["matrix"],
	t: TFunction
): ColumnsType<ReportRow>[number] {
	const sanalar = matritsaSanalari(matrix);
	const xodimlar = new Map(matrix.map((m) => [m.employeeId, sanaBoyichaHolat(m.days)]));

	return {
		title: t("table.dayStripColumn", { count: sanalar.length }),
		key: "__skudDayStrip",
		width: KUN_CHIZIGI_PX,
		render: (_qiymat: unknown, qator: ReportRow) => {
			const id = xodimId(qator);
			const holatlar = id ? xodimlar.get(id) : undefined;

			if (!holatlar) {
				return null;
			}

			return (
				<div style={{ width: KUN_CHIZIGI_PX }}>
					<DayStrip
						height={14}
						ariaLabel={t("table.dayStripAria")}
						cells={sanalar.map((sana) => {
							const holat = holatlar.get(sana);
							const yorliq = `${sana.slice(8, 10)}.${sana.slice(5, 7)}`;

							return holat === undefined
								? { color: "var(--chart-grid)", tooltip: `${yorliq} · ${BOSH}` }
								: {
										color: holatVar(ATTENDANCE_STATUS_RANG[holat] ?? "neytral"),
										tooltip: `${yorliq} · ${t(`analytics.breakdown.${holat}`)}`,
									};
						})}
					/>
				</div>
			);
		},
	};
}
